import { blinkConstants } from '@thoughtspot/blink-constants';
import { strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { ColorPalette } from './utils/color-palette';
import { FontFace, FontFaceFormat } from './utils/font-face';
import { GetBlobService } from './utils/get-blob-service';
export var AppLogoType;
(function (AppLogoType) {
    AppLogoType[AppLogoType["DEFAULT"] = 0] = "DEFAULT";
    AppLogoType[AppLogoType["GRAY"] = 1] = "GRAY";
    AppLogoType[AppLogoType["WIDE_BLACK"] = 2] = "WIDE_BLACK";
})(AppLogoType || (AppLogoType = {}));
export var CustomizableChartFeature;
(function (CustomizableChartFeature) {
    CustomizableChartFeature[CustomizableChartFeature["X_AXIS_LABEL"] = 0] = "X_AXIS_LABEL";
    CustomizableChartFeature[CustomizableChartFeature["X_AXIS_TITLE"] = 1] = "X_AXIS_TITLE";
    CustomizableChartFeature[CustomizableChartFeature["Y_AXIS_LABEL"] = 2] = "Y_AXIS_LABEL";
    CustomizableChartFeature[CustomizableChartFeature["Y_AXIS_TITLE"] = 3] = "Y_AXIS_TITLE";
    CustomizableChartFeature[CustomizableChartFeature["TOOLTIP"] = 4] = "TOOLTIP";
    CustomizableChartFeature[CustomizableChartFeature["SCATTER_CHART"] = 5] = "SCATTER_CHART";
    CustomizableChartFeature[CustomizableChartFeature["PIE_CHART"] = 6] = "PIE_CHART";
    CustomizableChartFeature[CustomizableChartFeature["LINE_CHART"] = 7] = "LINE_CHART";
    CustomizableChartFeature[CustomizableChartFeature["COLUMN_CHART"] = 8] = "COLUMN_CHART";
    CustomizableChartFeature[CustomizableChartFeature["BAR_CHART"] = 9] = "BAR_CHART";
    CustomizableChartFeature[CustomizableChartFeature["AREA_CHART"] = 10] = "AREA_CHART";
    CustomizableChartFeature[CustomizableChartFeature["TAIL_FEATURE"] = 11] = "TAIL_FEATURE";
})(CustomizableChartFeature || (CustomizableChartFeature = {}));
export var CustomizableTableFeature;
(function (CustomizableTableFeature) {
    CustomizableTableFeature[CustomizableTableFeature["CELL"] = 0] = "CELL";
    CustomizableTableFeature[CustomizableTableFeature["TAIL_FEATURE"] = 1] = "TAIL_FEATURE";
})(CustomizableTableFeature || (CustomizableTableFeature = {}));
export class CustomStyleBackground {
    constructor(color) {
        this.color = color;
    }
    static fromJson(json) {
        const color = json.color;
        return new CustomStyleBackground(color);
    }
    toJson() {
        return {
            color: this.color,
        };
    }
}
export class CustomStylePanelColor {
    constructor(color) {
        this.color = color;
    }
    static fromJson(json) {
        const color = json.color;
        return new CustomStylePanelColor(color);
    }
    toJson() {
        return {
            color: this.color,
        };
    }
}
export class CustomStylingConfig {
    constructor(version, customFontFaces, appLogoTypeToImage = {}, chartFeatureToFontGuid = {}, tableFeatureToFontGuid = {}, appBackground = null, appPanelColor = null, footerText = null, chartColorPalettes = [], pageTitle = null, disableColorRotation = false) {
        this.version = version;
        this.customFontFaces = customFontFaces;
        this.appLogoTypeToImage = appLogoTypeToImage;
        this.chartFeatureToFontGuid = chartFeatureToFontGuid;
        this.tableFeatureToFontGuid = tableFeatureToFontGuid;
        this.appBackground = appBackground;
        this.appPanelColor = appPanelColor;
        this.footerText = footerText;
        this.chartColorPalettes = chartColorPalettes;
        this.pageTitle = pageTitle;
        this.disableColorRotation = disableColorRotation;
        this.tableFeatureToDefaultFontFace = {};
        this.chartFeatureToDefaultFontFace = {};
        this.defaultColors = null;
        CustomStylingConfig.getAllCustomizableTableFeatures().forEach((tableFeature) => {
            this.tableFeatureToDefaultFontFace[tableFeature] = CustomStylingConfig.createNewDefaultTableFontFace(tableFeature);
        });
        CustomStylingConfig.getAllCustomizableChartFeatures().forEach((chartFeature) => {
            this.chartFeatureToDefaultFontFace[chartFeature] = CustomStylingConfig.createNewDefaultChartFontFace(chartFeature);
        });
        const defaultColorCodes = [
            CustomStylingConfig.Defaults.Chart.ColorPalette.Large.COLORS,
        ];
        this.defaultColors = defaultColorCodes.map((colorCodes) => {
            return colorCodes.map((colorCode) => colorCode);
        });
        CustomStylingConfig.sortChartColorPalettes(this.chartColorPalettes);
    }
    static getAllCustomizableTableFeatures() {
        const features = [];
        for (let feature = CustomizableTableFeature.CELL; feature < CustomizableTableFeature.TAIL_FEATURE; ++feature) {
            features.push(feature);
        }
        return features;
    }
    static getAllCustomizableChartFeatures() {
        const features = [];
        for (let feature = CustomizableChartFeature.X_AXIS_LABEL; feature < CustomizableChartFeature.TAIL_FEATURE; ++feature) {
            features.push(feature);
        }
        return features;
    }
    static fromJson(configJson) {
        const version = configJson.version;
        const appLogoTypeToGuid = configJson.appLogoTypeToImage || {};
        let customFontFaces = [];
        if (configJson.customFontFaces) {
            customFontFaces = configJson.customFontFaces.map((customFontFace) => FontFace.fromJson(customFontFace));
        }
        const chartFeatureToFontGuid = configJson.chartFeatureToFontGuid || {};
        const tableFeatureToFontGuid = configJson.tableFeatureToFontGuid || {};
        let appBackground = null;
        if (configJson.appBackground) {
            appBackground = CustomStyleBackground.fromJson(configJson.appBackground);
        }
        let appPanelColor = null;
        if (configJson.appPanelColor) {
            appPanelColor = CustomStylePanelColor.fromJson(configJson.appPanelColor);
        }
        let footerText = '';
        if (!!configJson.footerText && !!configJson.footerText) {
            footerText = configJson.footerText;
        }
        let chartColorsPalettes = [];
        if (configJson.chartColorPalettes) {
            chartColorsPalettes = configJson.chartColorPalettes.map((colorPalette) => ColorPalette.fromJson(colorPalette));
        }
        let pageTitle = blinkConstants.defaultProductName;
        if (configJson.pageTitle) {
            pageTitle = configJson.pageTitle;
        }
        let disableColorRotation = false;
        if (configJson.disableColorRotation) {
            disableColorRotation = configJson.disableColorRotation;
        }
        return new CustomStylingConfig(version, customFontFaces, appLogoTypeToGuid, chartFeatureToFontGuid, tableFeatureToFontGuid, appBackground, appPanelColor, footerText, chartColorsPalettes, pageTitle, disableColorRotation);
    }
    static convertChartFeatureToLabel(feature) {
        const constantsRoot = strings.styleCustomizer.customizableFeatures.chart;
        switch (feature) {
            case CustomizableChartFeature.X_AXIS_LABEL:
                return constantsRoot.X_AXIS_LABEL;
            case CustomizableChartFeature.X_AXIS_TITLE:
                return constantsRoot.X_AXIS_TITLE;
            case CustomizableChartFeature.Y_AXIS_LABEL:
                return constantsRoot.Y_AXIS_LABEL;
            case CustomizableChartFeature.Y_AXIS_TITLE:
                return constantsRoot.Y_AXIS_TITLE;
            case CustomizableChartFeature.TOOLTIP:
                return constantsRoot.TOOLTIP;
            case CustomizableChartFeature.SCATTER_CHART:
                return constantsRoot.SCATTER_CHART;
            case CustomizableChartFeature.PIE_CHART:
                return constantsRoot.PIE_CHART;
            case CustomizableChartFeature.LINE_CHART:
                return constantsRoot.LINE_CHART;
            case CustomizableChartFeature.COLUMN_CHART:
                return constantsRoot.COLUMN_CHART;
            case CustomizableChartFeature.BAR_CHART:
                return constantsRoot.BAR_CHART;
            case CustomizableChartFeature.AREA_CHART:
                return constantsRoot.AREA_CHART;
            default:
                return null;
        }
    }
    static covertTableFeatureToLabel(feature) {
        const constantsRoot = strings.styleCustomizer.customizableFeatures.table;
        switch (feature) {
            case CustomizableTableFeature.CELL:
                return constantsRoot.CELL;
            default:
                return null;
        }
    }
    static getPivotHeatmapColor(id) {
        id %= CustomStylingConfig.Defaults.Pivot.HEATMAP_COLORS.length;
        return CustomStylingConfig.Defaults.Pivot.HEATMAP_COLORS[id];
    }
    static getGeoBubbleColors() {
        return CustomStylingConfig.Defaults.Geo.COLORS.BUBBLE;
    }
    static getGeoAreaColors() {
        return CustomStylingConfig.Defaults.Geo.COLORS.AREA;
    }
    static getGradientColors() {
        return CustomStylingConfig.gradientMultiHueColors;
    }
    static getTableFont() {
        return CustomStylingConfig.Defaults.Table.FONT_FAMILY;
    }
    static getDefaultChartFontColor() {
        return CustomStylingConfig.Defaults.Chart.DEFAULT_FONT_COLOR;
    }
    static getRootElemFontSize() {
        const rootFontSize = getComputedStyle(document.documentElement).fontSize ||
            CustomStylingConfig.Defaults.BaseFontSize;
        return Number(rootFontSize.split('px')[0]);
    }
    static createNewDefaultTableFontFace(tableFeature) {
        const fontFace = new FontFace(null, CustomStylingConfig.Defaults.Table.FONT_FAMILY, FontFaceFormat.WOFF, null);
        fontFace.size = CustomStylingConfig.Defaults.Table.FONT_SIZE;
        return fontFace;
    }
    static createNewDefaultChartFontFace(chartFeature) {
        const fontFace = new FontFace(null, CustomStylingConfig.Defaults.Chart.DEFAULT_FONT_FAMILY, FontFaceFormat.WOFF);
        fontFace.color = CustomStylingConfig.Defaults.Chart.DEFAULT_FONT_COLOR;
        fontFace.size = CustomStylingConfig.Defaults.Chart.DEFAULT_FONT_SIZE;
        switch (chartFeature) {
            case CustomizableChartFeature.TOOLTIP:
                fontFace.family =
                    CustomStylingConfig.Defaults.Chart.LIGHT_FONT_FAMILY;
                fontFace.size =
                    CustomStylingConfig.Defaults.Chart.TOOLTIP_FONT_SIZE;
                break;
            case CustomizableChartFeature.X_AXIS_TITLE:
            case CustomizableChartFeature.Y_AXIS_TITLE:
                fontFace.family =
                    CustomStylingConfig.Defaults.Chart.LIGHT_FONT_FAMILY;
                fontFace.color =
                    CustomStylingConfig.Defaults.Chart.AXIS_FONT_COLOR;
                fontFace.size =
                    CustomStylingConfig.Defaults.Chart.AXIS_FONT_SIZE;
                break;
            default:
                break;
        }
        return fontFace;
    }
    static sortChartColorPalettes(colorPalettes) {
        colorPalettes.sort((colorPaletteA, colorPaletteB) => {
            return (colorPaletteA.getColors().length -
                colorPaletteB.getColors().length);
        });
    }
    getAllCustomFontFaces() {
        return this.customFontFaces.slice(0);
    }
    getAppLogoImageGuid(appLogoType) {
        const customImage = this.appLogoTypeToImage[appLogoType];
        if (customImage) {
            return customImage.guid;
        }
        return null;
    }
    hasCustomImageLogo(appLogoType) {
        return !!this.appLogoTypeToImage[appLogoType];
    }
    getAppLogoUrl(appLogoType, optionalLogo) {
        const customLogoImage = this.appLogoTypeToImage[appLogoType];
        if (customLogoImage) {
            return GetBlobService.getBlobUrl(customLogoImage.guid);
        }
        if (optionalLogo) {
            return optionalLogo;
        }
        switch (appLogoType) {
            case AppLogoType.WIDE_BLACK:
                return CustomStylingConfig.Defaults.WIDE_APP_LOGO_URL;
            case AppLogoType.GRAY:
                return CustomStylingConfig.Defaults.APP_LOGO_GRAY_URL;
            default:
                return CustomStylingConfig.Defaults.APP_LOGO_URL;
        }
    }
    setAppLogoImageGuid(appLogoImageGuid, appLogoType) {
        this.appLogoTypeToImage[appLogoType] = {
            guid: appLogoImageGuid,
        };
    }
    resetAppLogoImage(appLogoType) {
        delete this.appLogoTypeToImage[appLogoType];
    }
    getFaviconUrl(appLogoType) {
        const customLogoImage = this.appLogoTypeToImage[appLogoType];
        if (customLogoImage) {
            return GetBlobService.getBlobUrl(customLogoImage.guid);
        }
        return CustomStylingConfig.Defaults.APP_FAVICON;
    }
    getTableFontFace(tableFeature) {
        if (this.tableFeatureToFontGuid) {
            const fontGuid = this.tableFeatureToFontGuid[tableFeature];
            if (fontGuid) {
                return this.getFontFaceForGuid(fontGuid);
            }
        }
        return this.tableFeatureToDefaultFontFace[tableFeature];
    }
    setTableFont(fontFace, tableFeature) {
        if (fontFace.guid) {
            this.addOrUpdateFontFace(fontFace);
        }
        this.tableFeatureToFontGuid[tableFeature] = fontFace.guid;
    }
    resetTableFonts() {
        this.tableFeatureToFontGuid = {};
    }
    getChartFontFace(chartFeature) {
        if (this.chartFeatureToFontGuid) {
            const fontGuid = this.chartFeatureToFontGuid[chartFeature];
            if (fontGuid) {
                return this.getFontFaceForGuid(fontGuid);
            }
        }
        return this.chartFeatureToDefaultFontFace[chartFeature];
    }
    setChartFont(fontFace, chartFeature) {
        if (fontFace.guid) {
            this.addOrUpdateFontFace(fontFace);
        }
        this.chartFeatureToFontGuid[chartFeature] = fontFace.guid;
    }
    resetChartFonts() {
        this.chartFeatureToFontGuid = {};
    }
    addOrUpdateFontFace(fontFace) {
        if (fontFace.isLocal()) {
            return;
        }
        _.remove(this.customFontFaces, function (existingFontFace) {
            return fontFace.guid === existingFontFace.guid;
        });
        this.customFontFaces.unshift(fontFace);
    }
    getAppBackground() {
        if (!!this.appBackground && !!this.appBackground.color) {
            return this.appBackground;
        }
        return CustomStyleBackground.fromJson({
            color: CustomStylingConfig.Defaults.AppBackground.COLOR,
        });
    }
    setAppBackground(appBackground) {
        this.appBackground = appBackground;
    }
    resetAppBackground() {
        this.appBackground = null;
    }
    getAppPanelColor() {
        if (!!this.appPanelColor && !!this.appPanelColor.color) {
            return this.appPanelColor;
        }
        return CustomStylePanelColor.fromJson({
            color: CustomStylingConfig.Defaults.AppPanelColor.COLOR,
        });
    }
    setAppPanelColor(appPanelColor) {
        this.appPanelColor = appPanelColor;
    }
    resetAppPanelColor() {
        this.appPanelColor = null;
    }
    getFooterText() {
        return this.footerText;
    }
    setFooterText(footerText) {
        this.footerText = footerText;
    }
    resetFooterText() {
        this.footerText = '';
    }
    getChartColorPalettes() {
        if (this.chartColorPalettes && this.chartColorPalettes.length > 0) {
            let paletteToUse;
            if (this.chartColorPalettes.length === 2) {
                paletteToUse = this.chartColorPalettes[1];
            }
            else {
                paletteToUse = this.chartColorPalettes[0];
            }
            if (!paletteToUse) {
                return this.getDefaultColorPalettes();
            }
            paletteToUse.sanitizeColors(this.defaultColors[0]);
            this.chartColorPalettes = [paletteToUse];
            return this.chartColorPalettes;
        }
        return this.getDefaultColorPalettes();
    }
    setChartColorPalettes(colorPalettes) {
        this.chartColorPalettes = colorPalettes;
        CustomStylingConfig.sortChartColorPalettes(this.chartColorPalettes);
    }
    resetChartColorPalettes() {
        this.chartColorPalettes = [];
    }
    isColorRotationDisabled() {
        return this.disableColorRotation;
    }
    setColorRotationDisabled(disableColorRotation) {
        this.disableColorRotation = disableColorRotation;
    }
    resetColorRotation() {
        this.disableColorRotation = false;
    }
    getPageTitle() {
        return this.pageTitle;
    }
    setPageTitle(pageTitle) {
        this.pageTitle = pageTitle;
    }
    resetPageTitle() {
        this.pageTitle = strings.Page_title;
    }
    toJson() {
        const fontFaceJson = this.customFontFaces.map(function (customFontFace) {
            return customFontFace.toJson();
        });
        const chartColorPaletteJson = this.chartColorPalettes.map((chartColorPalette) => chartColorPalette.toJson());
        const appBackground = this.appBackground
            ? this.appBackground.toJson()
            : null;
        const appPanelColor = this.appPanelColor
            ? this.appPanelColor.toJson()
            : null;
        return {
            appLogoTypeToImage: this.appLogoTypeToImage,
            customFontFaces: fontFaceJson,
            chartFeatureToFontGuid: this.chartFeatureToFontGuid,
            tableFeatureToFontGuid: this.tableFeatureToFontGuid,
            appBackground,
            appPanelColor,
            footerText: this.footerText,
            chartColorPalettes: chartColorPaletteJson,
            disableColorRotation: this.disableColorRotation,
            pageTitle: this.pageTitle,
        };
    }
    getDefaultColorPalettes() {
        return this.defaultColors.map((defaultColorList) => new ColorPalette(defaultColorList));
    }
    getFontFaceForGuid(guid) {
        if (!guid || !this.customFontFaces) {
            return null;
        }
        return (this.customFontFaces.find(function (fontFace) {
            return fontFace.guid === guid;
        }) || null);
    }
}
CustomStylingConfig.singleHueColors = [
    blinkConstants.defaultColorPalette.BLUE,
    blinkConstants.defaultColorPalette.TEAL,
    blinkConstants.defaultColorPalette.YELLOW,
    blinkConstants.defaultColorPalette.GREEN,
    blinkConstants.defaultColorPalette.PURPLE,
    blinkConstants.defaultColorPalette.ORANGE,
    blinkConstants.defaultColorPalette.GREY,
    blinkConstants.defaultColorPalette.RED,
];
CustomStylingConfig.primaryColors = [
    blinkConstants.defaultColorPalette.RED[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.BLUE[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.GREEN[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.TEAL[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.ORANGE[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.PURPLE[blinkConstants.PRIMARY_COLOR_HUE_IDX],
    blinkConstants.defaultColorPalette.YELLOW[blinkConstants.PRIMARY_COLOR_HUE_IDX],
];
CustomStylingConfig.multiHueColors = [
    blinkConstants.multiHueColorsColorPalette.RED_YELLOW,
    blinkConstants.multiHueColorsColorPalette.BLUE,
    blinkConstants.multiHueColorsColorPalette.YELLOW_GREEN,
    blinkConstants.multiHueColorsColorPalette.PINK,
    blinkConstants.multiHueColorsColorPalette.YELLOW_GREEN_BLUE,
    blinkConstants.multiHueColorsColorPalette.REDS,
    blinkConstants.multiHueColorsColorPalette.PURPLES,
];
CustomStylingConfig.gradientMultiHueColors = [
    blinkConstants.gradientColorsColorPalette.BLACK,
    blinkConstants.gradientColorsColorPalette.RED,
    blinkConstants.gradientColorsColorPalette.ORANGE,
    blinkConstants.gradientColorsColorPalette.PURPLE,
    blinkConstants.gradientColorsColorPalette.GREEN,
    blinkConstants.gradientColorsColorPalette.YELLOW,
    blinkConstants.gradientColorsColorPalette.TEAL,
    blinkConstants.gradientColorsColorPalette.BLUE,
];
CustomStylingConfig.Defaults = {
    APP_LOGO_URL: 'resources/img/logo/TS-logo-white-no-bg.svg',
    APP_LOGO_GRAY_URL: 'resources/img/logo/TS-logo-gray-no-bg.svg',
    WIDE_APP_LOGO_URL: 'resources/img/logo/TS-logo-wide.svg',
    APP_FAVICON: 'resources/img/logo/favicon.svg',
    Table: {
        FONT_FAMILY: 'Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif',
        FONT_SIZE: 14,
    },
    Pivot: {
        HEATMAP_COLORS: [
            blinkConstants.defaultColorPalette.TEAL[0],
            blinkConstants.defaultColorPalette.YELLOW[0],
            blinkConstants.defaultColorPalette.BLUE[0],
            blinkConstants.defaultColorPalette.RED[0],
            blinkConstants.defaultColorPalette.GREEN[0],
            blinkConstants.defaultColorPalette.PURPLE[0],
            blinkConstants.defaultColorPalette.ORANGE[0],
            blinkConstants.defaultColorPalette.GREY[0],
        ],
    },
    Geo: {
        COLORS: {
            BUBBLE: CustomStylingConfig.primaryColors,
            AREA: CustomStylingConfig.multiHueColors,
        },
    },
    Chart: {
        DEFAULT_FONT_FAMILY: 'var(--ts-var-axis-data-label-font-family, Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif)',
        DEFAULT_FONT_SIZE: 12,
        DEFAULT_FONT_COLOR: 'var(--ts-var-axis-data-label-color, #5e5e5e)',
        LIGHT_FONT_FAMILY: 'Optimo-Plain, Helvetica Neue, Helvetica, Arial, sans-serif',
        AXIS_FONT_SIZE: 12,
        AXIS_FONT_COLOR: '#5e5e5e',
        TOOLTIP_FONT_SIZE: 11,
        ColorPalette: {
            Large: {
                COLORS: _.flatten(CustomStylingConfig.singleHueColors),
            },
        },
    },
    AppBackground: {
        COLOR: '#f6f8fa',
    },
    AppPanelColor: {
        COLOR: '#303540',
    },
    BaseFontSize: '16px',
};
//# sourceMappingURL=custom-style-config.js.map